import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Listing, listingsAPI } from '../api';

interface CartItem {
  listing: Listing;
  quantity: number;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (listing: Listing, quantity?: number) => void;
  removeFromCart: (listingId: number) => void;
  updateQuantity: (listingId: number, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  validateCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    // Load cart from localStorage on init
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        return JSON.parse(savedCart);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (listing: Listing, quantity: number = 1) => {
    listingsAPI.recordCartAdd(listing.id).catch(() => {});
    setCartItems(prev => {
      const existingItem = prev.find(item => item.listing.id === listing.id);
      if (existingItem) {
        // Update quantity if item already exists
        const maxQuantity = listing.quantity || Infinity;
        const newQuantity = Math.min(existingItem.quantity + quantity, maxQuantity);
        return prev.map(item =>
          item.listing.id === listing.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      } else {
        // Add new item
        return [...prev, { listing, quantity }];
      }
    });
  };

  const removeFromCart = (listingId: number) => {
    setCartItems(prev => prev.filter(item => item.listing.id !== listingId));
  };

  const updateQuantity = (listingId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(listingId);
      return;
    }
    setCartItems(prev =>
      prev.map(item => {
        if (item.listing.id === listingId) {
          const maxQuantity = item.listing.quantity || Infinity;
          return { ...item, quantity: Math.min(quantity, maxQuantity) };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalItems = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((sum, item) => {
      const basePrice = Number(item.listing.price) || 0;
      const quantity = item.quantity;
      let itemTotal = basePrice * quantity;
      
      // Apply discount if applicable
      if (item.listing.discount_type && item.listing.discount_value && item.listing.discount_min_quantity) {
        const minQuantity = item.listing.discount_min_quantity || 2;
        if (quantity >= minQuantity) {
          if (item.listing.discount_type === 'percentage') {
            const discountAmount = (basePrice * quantity) * (Number(item.listing.discount_value) / 100);
            itemTotal -= discountAmount;
          } else if (item.listing.discount_type === 'fixed') {
            const discountAmount = Number(item.listing.discount_value) * quantity;
            itemTotal -= discountAmount;
          }
        }
      }
      
      return sum + itemTotal;
    }, 0);
  };

  /** Remove items that are sold, deleted, or have 0 quantity; cap quantity to available. */
  const validateCart = useCallback(async () => {
    const current = cartItemsRef.current;
    if (current.length === 0) return;
    const results = await Promise.all(
      current.map(async (item): Promise<CartItem | null> => {
        try {
          const listing = await listingsAPI.getOne(item.listing.id);
          const sold = listing.sold === 1 || listing.sold === true;
          const available = Number(listing.quantity) ?? 0;
          if (sold || available < 1) return null;
          const quantity = Math.min(item.quantity, available);
          return { listing: { ...item.listing, ...listing }, quantity };
        } catch {
          return null;
        }
      })
    );
    const valid = results.filter((r): r is CartItem => r !== null);
    setCartItems(valid);
  }, []);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalItems,
        getTotalPrice,
        validateCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

